/**
 * Reading an export file into a plain table.
 *
 * Every test management system can write CSV or Excel, so those two formats
 * cover "import from another TMS" without a connector per vendor. The file is
 * read in the browser: the person sees what was understood before anything is
 * created, and the API receives test cases rather than a file format.
 */

import type { SheetData } from 'read-excel-file/browser';

/** The largest file worth reading in a browser tab. */
export const MAX_IMPORT_FILE_BYTES = 5_000_000;

export interface SheetTable {
  /** First row of the file, trimmed; the column names to map. */
  headers: string[];
  /** Data rows, each padded to the header width. */
  rows: string[][];
  /** Row number in the file of `rows[0]`, so a report can point at it. */
  firstRowNumber: number;
}

export class ImportFileError extends Error {
  /** A dictionary key, because the message is shown to the user. */
  constructor(readonly reason: 'empty' | 'tooLarge' | 'unsupported' | 'unreadable') {
    super(reason);
    this.name = 'ImportFileError';
  }
}

/**
 * Splits CSV text into rows of cells, honouring quoted fields — a step
 * description routinely contains commas, quotes and line breaks, and losing
 * them would quietly corrupt the import.
 */
export const parseCsv = (text: string, delimiter?: string): string[][] => {
  const content = text.replace(/^\uFEFF/, '');
  const separator = delimiter ?? detectDelimiter(content);

  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;

  for (let index = 0; index < content.length; index += 1) {
    const char = content[index];

    if (quoted) {
      if (char === '"') {
        // A doubled quote inside a quoted field is one literal quote.
        if (content[index + 1] === '"') {
          cell += '"';
          index += 1;
        } else {
          quoted = false;
        }
      } else {
        cell += char;
      }
      continue;
    }

    if (char === '"') {
      quoted = true;
    } else if (char === separator) {
      row.push(cell);
      cell = '';
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && content[index + 1] === '\n') index += 1;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += char;
    }
  }

  if (cell !== '' || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }

  return rows.filter((cells) => cells.some((value) => value.trim() !== ''));
};

/** Exports from European locales use `;`, and some tools use tabs. */
const detectDelimiter = (content: string): string => {
  const firstLine = content.slice(0, content.search(/\r?\n/) + 1 || undefined);
  const counts = [',', ';', '\t'].map((candidate) => ({
    candidate,
    count: firstLine.split(candidate).length - 1,
  }));
  const best = counts.reduce((winner, option) => (option.count > winner.count ? option : winner));
  return best.count > 0 ? best.candidate : ',';
};

/** Trims a parsed grid into headers plus rows of equal width. */
export const toTable = (grid: string[][]): SheetTable => {
  const [header, ...rest] = grid;
  if (!header || rest.length === 0) throw new ImportFileError('empty');

  const headers = header.map((value) => value.trim());
  const rows = rest.map((cells) =>
    headers.map((_, column) => (cells[column] ?? '').replace(/\r\n/g, '\n').trim()),
  );

  return { headers, rows, firstRowNumber: 2 };
};

const CSV_EXTENSIONS = ['.csv', '.tsv', '.txt'];
const EXCEL_EXTENSIONS = ['.xlsx', '.xlsm'];

export const readImportFile = async (file: File): Promise<SheetTable> => {
  if (file.size > MAX_IMPORT_FILE_BYTES) throw new ImportFileError('tooLarge');
  const name = file.name.toLowerCase();

  if (CSV_EXTENSIONS.some((extension) => name.endsWith(extension))) {
    return toTable(parseCsv(await readText(file)));
  }

  if (EXCEL_EXTENSIONS.some((extension) => name.endsWith(extension))) {
    // Loaded on demand: the reader is only needed by this dialog, and it is
    // larger than the screens around it. The first sheet is the export; a
    // workbook with several is not something these tools produce.
    const { readSheet } = await import('read-excel-file/browser');
    let grid: SheetData;
    try {
      // The overload taking a sheet number is the one that returns plain rows,
      // and the bytes are handed over directly so the reader never depends on
      // `Blob.arrayBuffer` being there.
      grid = await readSheet(await readBytes(file), 1);
    } catch {
      throw new ImportFileError('unreadable');
    }
    return toTable(grid.map((cells) => cells.map(cellToString)));
  }

  throw new ImportFileError('unsupported');
};

/** `Blob.arrayBuffer` and `Blob.text` are not everywhere yet, hence the reader. */
const readBytes = async (file: File): Promise<ArrayBuffer> => {
  if (typeof file.arrayBuffer === 'function') return file.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(new ImportFileError('unreadable'));
    reader.readAsArrayBuffer(file);
  });
};


const readText = async (file: File): Promise<string> => {
  if (typeof file.text === 'function') return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(new ImportFileError('unreadable'));
    reader.readAsText(file);
  });
};

/** Excel hands back numbers and dates; the import works in text. */
const cellToString = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return String(value);
};
